import React, { useContext } from 'react';
import { LangContext } from '../context/LangContext';

/**
 * 🚦 Ampel-Filter für Program-Listings
 * Ermöglicht Filterung nach Aufwand-Kategorien
 */
const EffortAmpelFilter = ({ programs = [], filterLevel, setFilterLevel, sortBy, setSortBy }) => {
  // Import translations from context
  const { t } = useContext(LangContext);
  
  // Mehrsprachige Filter-Labels
  const filterLabels = {
    all: t('ampel.filterAll'),
    green: t('ampel.filterGreen'),
    yellow: t('ampel.filterYellow'),
    orange: t('ampel.filterOrange'),
    red: t('ampel.filterRed')
  };

  const sortLabels = {
    roi: t('ampel.sortROI'),
    effort: t('ampel.sortEffort'),
    deadline: t('ampel.sortDeadline')
  };


  // Statistiken berechnen
  const stats = React.useMemo(() => {
    const counts = { all: programs.length, green: 0, yellow: 0, orange: 0, red: 0 };
    programs.forEach(p => {
      if (p.effort?.level) counts[p.effort.level]++;
    });
    return counts;
  }, [programs]);

  // Quick Stats berechnen
  const quickStats = React.useMemo(() => {
    const topRecommendations = programs.filter(p => 
      p.effort?.roiBadge?.text?.includes('TOP') || 
      p.effort?.roiBadge?.text?.includes('RECOMENDACIÓN') ||
      p.effort?.roiBadge?.text?.includes('RACCOMANDAZIONE')
    ).length;
    
    const urgent = programs.filter(p => p.effort?.timeline?.isUrgent).length;
    
    const avgRoi = programs.length > 0 
      ? Math.round(programs.reduce((acc, p) => acc + (p.effort?.roi?.ratio || 0), 0) / programs.length)
      : 0;

    return { topRecommendations, urgent, avgRoi };
  }, [programs]);

  return (
    <div className="bg-white rounded-lg shadow-sm p-4 mb-4">
      <div className="flex flex-col sm:flex-row gap-4">
        {/* Ampel-Filter Buttons */}
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={() => setFilterLevel('all')}
            className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
              filterLevel === 'all' 
                ? 'bg-gray-800 text-white' 
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {filterLabels.all} ({stats.all})
          </button>
          
          <button
            onClick={() => setFilterLevel('green')}
            className={`px-3 py-2 rounded-md text-sm font-medium transition-colors flex items-center gap-1 ${
              filterLevel === 'green' 
                ? 'bg-green-600 text-white' 
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            🟢 {filterLabels.green} ({stats.green})
          </button>
          
          <button
            onClick={() => setFilterLevel('yellow')}
            className={`px-3 py-2 rounded-md text-sm font-medium transition-colors flex items-center gap-1 ${
              filterLevel === 'yellow' 
                ? 'bg-yellow-600 text-white' 
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            🟡 {filterLabels.yellow} ({stats.yellow})
          </button>
          
          <button
            onClick={() => setFilterLevel('orange')}
            className={`px-3 py-2 rounded-md text-sm font-medium transition-colors flex items-center gap-1 ${
              filterLevel === 'orange' 
                ? 'bg-orange-600 text-white' 
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            🟠 {filterLabels.orange} ({stats.orange})
          </button>
          
          <button
            onClick={() => setFilterLevel('red')}
            className={`px-3 py-2 rounded-md text-sm font-medium transition-colors flex items-center gap-1 ${
              filterLevel === 'red' 
                ? 'bg-red-600 text-white' 
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            🔴 {filterLabels.red} ({stats.red})
          </button>
        </div>

        {/* Sort Dropdown */}
        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-md text-sm min-w-0 flex-shrink-0"
        >
          <option value="roi">{sortLabels.roi}</option>
          <option value="effort">{sortLabels.effort}</option>
          <option value="deadline">{sortLabels.deadline}</option>
        </select>
      </div>

      {/* Quick Stats */}
      <div className="mt-4 flex flex-wrap gap-4 text-sm text-gray-600">
        <span className="flex items-center gap-1">
          🏆 {quickStats.topRecommendations} {t('ampel.topRecommendations')}
        </span>
        <span className="flex items-center gap-1">
          ⚠️ {quickStats.urgent} {t('ampel.urgent')}
        </span>
        <span className="flex items-center gap-1">
          💰 {t('ampel.avgROI')}: {quickStats.avgRoi}:1
        </span>
      </div>
    </div>
  );
};

export default EffortAmpelFilter;
